import { useState, useCallback } from 'react'
import {
  ColDef,
  FilterChangedEvent,
  GridApi,
  SortChangedEvent,
} from 'ag-grid-community'
import { SortedColumnState } from '../../../model/bulkSheetColumnSortState'

export const useTicketTypeFilters = (gridApi?: GridApi) => {
  const [filteredColumns, setFilteredColumns] = useState<ColDef[]>([])
  const [sortColumnsState, setSortColumnsState] = useState<SortedColumnState[]>(
    []
  )

  /**
   * フィルター変更時のハンドラ
   */
  const handleFilterChanged = useCallback((event: FilterChangedEvent) => {
    const filterModel = event.api.getFilterModel()
    const filteredCols: ColDef[] = []

    Object.keys(filterModel).forEach(colId => {
      const colDef = event.api.getColumnDef(colId)
      if (colDef) {
        filteredCols.push(colDef)
      }
    })

    setFilteredColumns(filteredCols)

    // フィルターまたはソートが適用されている場合はドラッグを無効化
    const columnState = event.api?.getColumnState() || []
    const hasSortedColumns = columnState.some(col => col.sort != null)
    const hasFilters = Object.keys(filterModel).length > 0
    event.api.setGridOption('suppressRowDrag', hasSortedColumns || hasFilters)
  }, [])

  /**
   * フィルター削除
   */
  const handleDeleteFilteredColumn = useCallback(
    (columnDef: ColDef) => {
      if (!columnDef || !gridApi) return

      const filterModel = gridApi.getFilterModel()
      const colId = columnDef.colId || columnDef.field || ''
      delete filterModel[colId]
      gridApi.setFilterModel(filterModel)
    },
    [gridApi]
  )

  /**
   * 全フィルターリセット
   */
  const resetFilters = useCallback(() => {
    if (!gridApi) return
    gridApi.setFilterModel(null)
  }, [gridApi])

  /**
   * ソート変更時のハンドラ
   */
  const handleSortChanged = useCallback((event: SortChangedEvent) => {
    // ソート中の列を取得
    const sortedColumns = event.api
      .getColumnState()
      .filter(col => !!col.sort)
      .map(col => event.api.getColumnDef(col.colId))
      .filter((v): v is ColDef => v !== null)

    // ColumnStateからソート情報を取得
    const columnState = event.api?.getColumnState() || []
    const sortedColumnState: { [colId: string]: any } = {}
    columnState.forEach(state => {
      if (state.sort) {
        sortedColumnState[state.colId] = state
      }
    })

    // SortedColumnStateの配列を作成
    const sortedColumnsState: SortedColumnState[] = sortedColumns.map(col => ({
      colId: col.colId,
      field: col.field,
      headerName: col.headerName,
      sort: col.colId ? sortedColumnState[col.colId]?.sort : null,
    }))

    setSortColumnsState(sortedColumnsState)

    // ドラッグアンドドロップの無効化制御（ソートまたはフィルター適用時）
    const filterModel = event.api.getFilterModel()
    const hasFilters = Object.keys(filterModel).length > 0
    event.api.setGridOption(
      'suppressRowDrag',
      0 < sortedColumns.length || hasFilters
    )
  }, [])

  /**
   * ソート列削除
   */
  const handleDeleteSortedColumn = useCallback(
    (column: string | ColDef) => {
      if (!gridApi) return

      const colId = typeof column === 'string' ? column : column.colId || ''
      gridApi.applyColumnState({
        state: [{ colId, sort: null }],
      })
    },
    [gridApi]
  )

  /**
   * 全ソート削除
   */
  const handleDeleteSortedAllColumns = useCallback(() => {
    if (!gridApi) return

    gridApi.applyColumnState({
      defaultState: { sort: null },
    })
  }, [gridApi])

  /**
   * ソート状態変更
   */
  const handleChangeSortColumnState = useCallback(
    (column: string | ColDef, sort: 'asc' | 'desc' | null) => {
      if (!gridApi) return

      const colId = typeof column === 'string' ? column : column.colId || ''
      gridApi.applyColumnState({
        state: [{ colId, sort }],
      })
    },
    [gridApi]
  )

  return {
    filteredColumns,
    sortColumnsState,
    handleFilterChanged,
    handleDeleteFilteredColumn,
    resetFilters,
    handleSortChanged,
    handleDeleteSortedColumn,
    handleDeleteSortedAllColumns,
    handleChangeSortColumnState,
  }
}
